import React, { useEffect, useState, useMemo } from 'react'
import { AppUseSelector } from '../../redux/hooks'
import { removeSpaceBetweenWords, renderConverterWord, wordSimplified } from 'utils/helperFunction'
import { chineseLang } from '../../i18n/chinese'

interface WordProps {
  index?: any
  text?: any
  onClick?: any
  isActive?: boolean
  isNewWord?: boolean
  isLearningWord?: boolean
  isOldWord?: boolean
  isGrowingWord?: boolean
  isDevelopingWord?: boolean
  isMasteredWord?: boolean
  pinYinText?: any
  pinyinFontSize?: any
  chineseWordFontSize?: any
  textObj?: any
  sentence?: any
}

const Word: React.FC<WordProps> = ({
  index,
  text,
  isNewWord,
  isLearningWord,
  isOldWord,
  isGrowingWord,
  isDevelopingWord,
  isMasteredWord,
  isActive,
  onClick,
  pinYinText,
  pinyinFontSize,
  chineseWordFontSize,
  textObj,
  sentence,
}: any) => {
  const [showPinyin, setShowPinyin] = useState(false)
  const { credentials } = AppUseSelector(({ user }) => user)
  const characterPreference = credentials?.customFields?.character
  const { pinyin_settings } = AppUseSelector(({ story }) => story)
  const wordWithOutType =
    !isNewWord && !isLearningWord && !isOldWord && !isGrowingWord && !isDevelopingWord && !isMasteredWord

  const currentSetting = useMemo(() => {
    if (isNewWord) return pinyin_settings?.new_word
    if (isLearningWord || isOldWord) return pinyin_settings?.recently_learned_word
    if (isGrowingWord || isDevelopingWord || isMasteredWord || wordWithOutType) {
      return pinyin_settings?.known_word
    }
    return null
  }, [
    pinyin_settings,
    isNewWord,
    isLearningWord,
    isOldWord,
    isGrowingWord,
    isDevelopingWord,
    isMasteredWord,
    wordWithOutType,
  ])

  const displayMode = useMemo(() => {
    if (currentSetting === 'never') return 'never'
    if (currentSetting === 'always') return 'always'
    if (currentSetting === 'on_hover') return 'hover'
    return null
  }, [currentSetting])

  useEffect(() => {
    switch (displayMode) {
      case 'never':
        setShowPinyin(false)
        break
      case 'always':
        setShowPinyin(true)
        break
      case 'hover':
        setShowPinyin(false)
        break
      default:
        setShowPinyin(false)
    }
  }, [displayMode])

  const handleMouseOver = () => {
    if (displayMode === 'hover') {
      setShowPinyin(true)
    }
  }

  const handleMouseOut = () => {
    if (displayMode === 'hover') {
      setShowPinyin(false)
    }
  }

  const pocketKey = useMemo(() => (typeof text === 'string' ? wordSimplified(text) : undefined), [text])

  const onWordClick = (e) => {
    onClick && onClick(e, textObj, sentence, index)
  }
  const className =
    `${onClick ? `word-translate` : ''} ` +
    `${isActive ? 'word-active' : ''} ` +
    `${isNewWord ? 'newword-highlight' : ''}` +
    `${isLearningWord ? 'learningword-highlight' : ''}` +
    `${isOldWord ? 'oldword-highlight' : ''}` +
    `${isGrowingWord ? 'growingword-highlight' : ''}` +
    `${isDevelopingWord ? 'developingword-highlight' : ''}` +
    `${isMasteredWord ? 'masterword-highlight' : ''}`

  // Determine if this is in a quiz result, corrections, or other section based on the index
  const isInSpecialSection =
    index?.includes('QUIZ-RESULT') ||
    index?.includes('USER-ANSWER') ||
    index?.includes('TEACHER-ANSWER') ||
    index?.includes('QUESTION-ANSWER-CONTENT')

  // Use 9.5px for pinyin in special sections, otherwise use the provided size or default
  const pinyinSize = isInSpecialSection ? '9.5px' : pinyinFontSize || '12px'

  // translate='no' keeps browser page translation (Chrome, Safari, Edge) off the
  // hanzi and pinyin. Each word is its own span, so a translated page swaps every
  // word for an isolated English gloss (像 became "picture") and the text stops
  // being Chinese. The English around it still translates.
  return (
    <span
      id={index}
      className={`d-inline-grid ${pinYinText ? 'ml-2 mr-2' : ''}`}
      onClick={onWordClick}
      translate='no'
      lang={chineseLang(characterPreference)}
    >
      <span className={'word-container'}>
        {pinYinText && (
          <span
            style={{
              fontSize: pinyinSize,
              lineHeight: '1.5',
              letterSpacing: '1.5px',
              visibility: showPinyin ? 'visible' : 'hidden',
            }}
            className={`pinyin-word m-al-c main_title_small_text`}
          >
            {removeSpaceBetweenWords(pinYinText)}
          </span>
        )}
        <span
          style={{
            fontSize: chineseWordFontSize || '20px',
            lineHeight: chineseWordFontSize && '1.2',
            letterSpacing: '0px',
          }}
          data-specific-buttons
          // The word pocket underlines every copy of a pocketed word by matching this key.
          data-w={pocketKey}
          className={`${className} m-al-c main_title_big_text`}
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
        >
          {renderConverterWord(text, characterPreference)}
        </span>
      </span>
    </span>
  )
}
export default Word
